import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Check, Clapperboard, Loader2, UserPlus } from 'lucide-react'

import { acceptProductionInvite, getProductionInvitePreview, type ProductionInvitePreview } from '@/api/studio-production'
import { useAuth } from '@/hooks'
import { PageContainer } from '@/shared/components/PageContainer'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { buildLoginForStudioPath } from '@/features/studio/lib/studio-auth-url'
import { ProtoBadge, ProtoBtn, ProtoCard, protoBtnClassName } from '@/features/studio/components/ui-kit'

export function InviteAcceptPage() {
  const { token = '' } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [preview, setPreview] = useState<ProductionInvitePreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const data = await getProductionInvitePreview(token)
        if (!cancelled) setPreview(data)
      } catch (e) {
        if (!cancelled) setError(resolveApiErrorMessage(e, 'invite.loadFailed'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (token) void load()
    return () => {
      cancelled = true
    }
  }, [token])

  async function handleAccept() {
    setAccepting(true)
    setError(null)
    try {
      await acceptProductionInvite(token)
      navigate(`/studio/productions/${preview?.production_public_id ?? ''}`)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'invite.acceptFailed'))
    } finally {
      setAccepting(false)
    }
  }

  const loginHref = buildLoginForStudioPath(`/studio/invites/${token}`)
  const needsLogin = !isAuthenticated

  return (
    <PageContainer size="sm">
      <div className="mx-auto max-w-md pt-12 text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-500/15">
          <Clapperboard className="h-8 w-8 text-indigo-400" />
        </div>
        <h1 className="text-2xl font-bold text-zinc-50">{t('invite.title')}</h1>
        <p className="mt-2 text-sm text-zinc-500">{t('invite.subtitle')}</p>

        {loading ? (
          <div className="mt-10 flex justify-center text-zinc-500">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : preview ? (
          <ProtoCard className="mt-8 text-left">
            <p className="text-lg font-semibold text-zinc-100">{preview.production_title}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {preview.department_name ? <ProtoBadge tone="indigo">{preview.department_name}</ProtoBadge> : null}
              <ProtoBadge tone="sky">{t(`production.roles.${preview.role}`, { defaultValue: preview.role })}</ProtoBadge>
            </div>
            <p className="mt-4 text-[11px] text-zinc-500">
              {t('invite.expires', { date: new Date(preview.expires_at).toLocaleString() })}
            </p>
          </ProtoCard>
        ) : null}

        {error ? <p className="mt-4 text-sm text-rose-300">{t(error, { defaultValue: error })}</p> : null}

        <div className="mt-6 flex flex-col gap-3">
          {needsLogin ? (
            <Link to={loginHref} className={protoBtnClassName({ size: 'sm', className: 'w-full' })}>
              <UserPlus className="h-4 w-4" />
              {t('invite.loginToAccept')}
            </Link>
          ) : (
            <ProtoBtn size="sm" className="w-full" disabled={!preview || accepting || preview.status !== 'pending'} onClick={() => void handleAccept()}>
              {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {accepting ? t('invite.accepting') : t('invite.accept')}
            </ProtoBtn>
          )}
          {!needsLogin ? (
            <Link to={loginHref} className={protoBtnClassName({ size: 'sm', variant: 'secondary', className: 'w-full' })}>
                <UserPlus className="h-4 w-4" />
                {t('invite.loginOther')}
            </Link>
          ) : null}
        </div>
      </div>
    </PageContainer>
  )
}
